/**
 * HistoryScreen — Practice analytics dashboard.
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import { useState, useMemo, type Dispatch, type SetStateAction } from "react";
import type { RetreatSession } from "@retreat/core";
import { derivePrideStats } from "../utils/prideStats";
import { fmtDurationCompact, toDateKey, dateKeyToDate } from "../utils/dateFormatting";
import { SESSION_STATUS } from "../constants";
import { isE2E } from "../utils/env";
import styles from "./HistoryScreen.module.css";
import { TEST_IDS } from "../testing/testIds";

// ── Local formatting helpers ─────────────────────────────────────────────────

const fmtDuration = fmtDurationCompact;

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtYear(ts: number): string {
  const y = new Date(ts).getFullYear();
  return y !== new Date().getFullYear() ? String(y) : "";
}

// ── Hero stats ──────────────────────────────────────────────────────────────

function computeHeroStats(sessions: RetreatSession[]) {
  const finished = sessions.filter((s) => s.status !== SESSION_STATUS.IN_PROGRESS);
  const completedCount = finished.filter((s) => s.status === SESSION_STATUS.COMPLETED).length;
  let totalSitting = 0;
  let longest = 0;
  let longestSession: RetreatSession | null = null;
  for (const s of finished) {
    const stats = derivePrideStats(s.containers);
    totalSitting += stats.meditationSeconds;
    const total = s.containers.reduce((a, c) => a + c.actualDurationSeconds, 0);
    if (total > longest) {
      longest = total;
      longestSession = s;
    }
  }
  return { completedCount, totalSitting, longest, longestSession };
}

// ── Heatmap ─────────────────────────────────────────────────────────────────

type HeatmapDay = { key: string; count: number; isToday: boolean };
type MonthLabel = { col: number; label: string };

function buildHeatmap(sessions: RetreatSession[], weeks = 12): HeatmapDay[] {
  const countByDay = new Map<string, number>();
  for (const s of sessions) {
    const key = toDateKey(s.startedAt);
    countByDay.set(key, (countByDay.get(key) ?? 0) + 1);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = toDateKey(today.getTime());
  const days: HeatmapDay[] = [];
  for (let i = weeks * 7 - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = toDateKey(d.getTime());
    days.push({ key, count: countByDay.get(key) ?? 0, isToday: key === todayKey });
  }
  return days;
}

function buildMonthLabels(days: HeatmapDay[]): MonthLabel[] {
  const result: MonthLabel[] = [];
  let lastMonth = '';
  days.forEach((day, i) => {
    const m = day.key.slice(0, 7);
    if (m !== lastMonth) {
      result.push({
        col: Math.floor(i / 7),
        label: dateKeyToDate(day.key).toLocaleDateString(undefined, { month: 'short' }),
      });
      lastMonth = m;
    }
  });
  return result;
}

function heatmapLevel(count: number): 0 | 1 | 2 | 3 {
  return Math.min(count, 3) as 0 | 1 | 2 | 3;
}

function Heatmap({
  sessions,
  onSelectSession,
}: {
  sessions: RetreatSession[];
  onSelectSession: (s: RetreatSession) => void;
}) {
  const days = buildHeatmap(sessions);
  const monthLabels = buildMonthLabels(days);
  const [selected, setSelected] = useState<HeatmapDay | null>(null);

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, RetreatSession[]>();
    for (const s of sessions) {
      const key = toDateKey(s.startedAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return map;
  }, [sessions]);

  function handleCellClick(day: HeatmapDay) {
    if (day.count === 0) {
      setSelected((prev) => (prev?.key === day.key ? null : day));
      return;
    }
    const daySessions = sessionsByDay.get(day.key) ?? [];
    if (daySessions.length === 1) {
      onSelectSession(daySessions[0]);
    } else if (selected?.key === day.key) {
      onSelectSession(daySessions[0]);
    } else {
      setSelected(day);
    }
  }

  return (
    <div className={styles.heatmapWrap} data-testid={TEST_IDS.HISTORY_HEATMAP} aria-label="Practice calendar — last 3 months">
      {/* Month labels */}
      <div className={styles.heatmapMonths}>
        {monthLabels.map(({ col, label }) => (
          <span
            key={label}
            className={styles.heatmapMonthLabel}
            style={{ gridColumnStart: col + 1 }}
          >
            {label}
          </span>
        ))}
      </div>

      {/* Calendar grid — full-width, square cells via aspect-ratio */}
      <div className={styles.heatmap}>
        {days.map((d) => (
          <button
            key={d.key}
            type="button"
            className={styles.heatmapCell}
            data-level={heatmapLevel(d.count)}
            data-today={d.isToday ? "" : undefined}
            aria-label={d.count > 0 ? `${d.count} session${d.count > 1 ? "s" : ""} on ${d.key}` : `No sessions on ${d.key}`}
            onClick={() => handleCellClick(d)}
          />
        ))}
      </div>

      {/* Tap callout */}
      {selected && (
        <div className={styles.heatmapCallout}>
          {fmtDate(dateKeyToDate(selected.key).getTime())}
          {" · "}
          {selected.count > 0
            ? `${selected.count} session${selected.count > 1 ? "s" : ""} · tap again to open`
            : "No practice"}
        </div>
      )}

      {/* Legend */}
      <div className={styles.heatmapLegend}>
        <span className={styles.legendLabel}>less</span>
        <div className={styles.legendCells}>
          {([0, 1, 2, 3] as const).map((l) => (
            <div key={l} className={styles.legendCell} data-level={l} />
          ))}
        </div>
        <span className={styles.legendLabel}>more</span>
      </div>
    </div>
  );
}

// ── Year/month grouping ──────────────────────────────────────────────────────

type MonthGroup = { monthKey: string; label: string; sessions: RetreatSession[] };
type YearGroup  = { year: number; months: MonthGroup[]; totalCount: number };

function groupByYear(sessions: RetreatSession[]): YearGroup[] {
  const byYear = new Map<number, Map<string, RetreatSession[]>>();
  for (const s of sessions) {
    const d = new Date(s.startedAt);
    const year = d.getFullYear();
    const monthKey = `${year}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!byYear.has(year)) byYear.set(year, new Map());
    const m = byYear.get(year)!;
    if (!m.has(monthKey)) m.set(monthKey, []);
    m.get(monthKey)!.push(s);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, monthMap]) => {
      const months = [...monthMap.entries()]
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([monthKey, monthSessions]) => ({
          monthKey,
          label: new Date(monthSessions[0].startedAt).toLocaleDateString("en-US", { month: "long" }),
          sessions: monthSessions,
        }));
      return { year, months, totalCount: months.reduce((sum, m) => sum + m.sessions.length, 0) };
    });
}

// ── Set toggle helper ────────────────────────────────────────────────────────
// Shared by year and month collapse — both toggle a value in/out of a Set.

function toggleItem<T>(setter: Dispatch<SetStateAction<Set<T>>>, value: T): void {
  setter(prev => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });
}

// ── Chevron ──────────────────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
      width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Completion bar ───────────────────────────────────────────────────────────

function CompletionBar({ actual, planned }: { actual: number; planned: number }) {
  const pct = planned > 0 ? Math.min(100, (actual / planned) * 100) : 0;
  return (
    <div className={styles.completionTrack} aria-hidden>
      <div className={styles.completionFill} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ── Session card ─────────────────────────────────────────────────────────────

function SessionCard({
  session,
  index,
  onSelect,
}: {
  session: RetreatSession;
  index: number;
  onSelect: (s: RetreatSession) => void;
}) {
  const stats = derivePrideStats(session.containers);
  const actualTotal = session.containers.reduce((a, c) => a + c.actualDurationSeconds, 0);
  const plannedTotal = session.totalScheduledSeconds
    ?? session.containers.reduce((a, c) => a + c.scheduledDurationSeconds, 0);
  const statusClass =
    session.status === SESSION_STATUS.COMPLETED ? styles.cardComplete : styles.cardIncomplete;
  const badgeClass =
    session.status === SESSION_STATUS.COMPLETED ? styles.badgeComplete : styles.badgeIncomplete;
  const badgeLabel =
    session.status === SESSION_STATUS.COMPLETED ? "completed" :
    session.status === SESSION_STATUS.IN_PROGRESS ? "in progress" : "ended early";
  const year = fmtYear(session.startedAt);

  return (
    <button
      className={`${styles.card} ${statusClass}`}
      onClick={() => onSelect(session)}
      type="button"
      data-testid={TEST_IDS.HISTORY_SESSION_CARD}
      data-session-index={index}
    >
      <div className={styles.cardTop}>
        <div className={styles.cardLeft}>
          <span className={styles.cardName}>{session.retreatName}</span>
          <span className={styles.cardDate}>
            {fmtDate(session.startedAt)}
            {year && <span className={styles.cardYear}> {year}</span>}
          </span>
        </div>
        <div className={styles.cardRight}>
          <span className={styles.cardDuration}>{fmtDuration(actualTotal)}</span>
          <span className={`${styles.cardBadge} ${badgeClass}`}>{badgeLabel}</span>
        </div>
      </div>

      <CompletionBar actual={actualTotal} planned={plannedTotal} />

      <div className={styles.cardActivity}>
        {stats.meditationSeconds > 0 && (
          <span className={styles.activityChip} data-type="meditation">
            {fmtDuration(stats.meditationSeconds)} sitting
          </span>
        )}
        {stats.walkingSeconds > 0 && (
          <span className={styles.activityChip} data-type="walking">
            {fmtDuration(stats.walkingSeconds)} walking
          </span>
        )}
        {stats.sleepSeconds > 0 && (
          <span className={styles.activityChip} data-type="sleep">
            {fmtDuration(stats.sleepSeconds)} sleep
          </span>
        )}
      </div>
    </button>
  );
}

// ── Filter type ──────────────────────────────────────────────────────────────

type Filter = "all" | typeof SESSION_STATUS.COMPLETED | "attempted";

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = {
  sessions: RetreatSession[];
  onBack: () => void;
  onSelectSession: (session: RetreatSession) => void;
  onOpenSettings?: () => void;
};

// ── Main component ────────────────────────────────────────────────────────────

export function HistoryScreen({ sessions, onBack, onSelectSession, onOpenSettings }: Props) {
  const [filter, setFilter] = useState<Filter>("all");

  // All periods start collapsed; in E2E mode auto-expand the most recent year/month
  // so tests can reach session cards without driving the collapse UI.
  const [expandedYears, setExpandedYears] = useState<Set<number>>(() => {
    if (!isE2E() || sessions.length === 0) return new Set<number>();
    const latest = sessions.reduce((a, b) => (a.startedAt > b.startedAt ? a : b));
    return new Set([new Date(latest.startedAt).getFullYear()]);
  });
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(() => {
    if (!isE2E() || sessions.length === 0) return new Set<string>();
    const latest = sessions.reduce((a, b) => (a.startedAt > b.startedAt ? a : b));
    const d = new Date(latest.startedAt);
    return new Set([`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`]);
  });

  const toggleYear  = (year: number) => toggleItem(setExpandedYears, year);
  const toggleMonth = (key: string)  => toggleItem(setExpandedMonths, key);

  const heroStats = computeHeroStats(sessions);

  const filtered = sessions.filter((s) => {
    if (filter === SESSION_STATUS.COMPLETED) return s.status === SESSION_STATUS.COMPLETED;
    if (filter === "attempted") return s.status === SESSION_STATUS.INCOMPLETE || s.status === SESSION_STATUS.IN_PROGRESS;
    return true;
  });

  const yearGroups = useMemo(() => groupByYear(filtered), [filtered]);

  return (
    <div className={styles.screen} data-testid={TEST_IDS.HISTORY_SCREEN}>
      <div className={styles.meta}>
        <button
          className={styles.backBtn}
          onClick={onBack}
          type="button"
          aria-label="Back"
          data-testid={TEST_IDS.BACK_BTN}
        >
          ← back
        </button>
        <span className={styles.metaLabel}>History</span>
        {onOpenSettings && (
          <button
            className={styles.gearBtn}
            onClick={onOpenSettings}
            type="button"
            aria-label="Settings"
            data-testid={TEST_IDS.HISTORY_SETTINGS_GEAR}
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      <div className={styles.content}>
        <h1 className={styles.heading}>Practice</h1>

        <div className={styles.desktopGrid}>
          {/* Left column on desktop: stats + heatmap */}
          <div className={styles.desktopLeft}>
            {sessions.length > 0 && (
              <div className={styles.heroStrip}>
                <button
                  className={styles.heroStatBtn}
                  onClick={() => setFilter(SESSION_STATUS.COMPLETED)}
                  type="button"
                  aria-label="Filter to completed sessions"
                  data-testid={TEST_IDS.HISTORY_STAT_COMPLETED}
                >
                  <span className={styles.heroNum}>{heroStats.completedCount}</span>
                  <span className={styles.heroLabel}>completed ↓</span>
                </button>
                <div className={styles.heroDivider} />
                <div className={styles.heroStat}>
                  <span className={styles.heroNum}>{fmtDuration(heroStats.totalSitting)}</span>
                  <span className={styles.heroLabel}>sitting</span>
                </div>
                <div className={styles.heroDivider} />
                <button
                  className={styles.heroStatBtn}
                  onClick={() => heroStats.longestSession && onSelectSession(heroStats.longestSession)}
                  type="button"
                  aria-label="Open longest session"
                  disabled={!heroStats.longestSession}
                  data-testid={TEST_IDS.HISTORY_STAT_LONGEST}
                >
                  <span className={styles.heroNum}>{fmtDuration(heroStats.longest)}</span>
                  <span className={styles.heroLabel}>longest ↗</span>
                </button>
              </div>
            )}

            {sessions.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Last 3 months</div>
                <Heatmap sessions={sessions} onSelectSession={onSelectSession} />
              </div>
            )}
          </div>

          {/* Right column on desktop: session list */}
          <div className={styles.desktopRight}>
            {sessions.length > 0 && (
              <div className={styles.segmentRow} role="group" aria-label="Filter retreats">
                <button
                  className={`${styles.segmentBtn} ${filter === "all" ? styles.segmentActive : ""}`}
                  onClick={() => setFilter("all")}
                  type="button"
                  data-testid={TEST_IDS.HISTORY_SEGMENT_ALL}
                >
                  All
                </button>
                <button
                  className={`${styles.segmentBtn} ${filter === SESSION_STATUS.COMPLETED ? styles.segmentActive : ""}`}
                  onClick={() => setFilter("completed")}
                  type="button"
                  data-testid={TEST_IDS.HISTORY_SEGMENT_COMPLETED}
                >
                  Completed
                </button>
                <button
                  className={`${styles.segmentBtn} ${filter === "attempted" ? styles.segmentActive : ""}`}
                  onClick={() => setFilter("attempted")}
                  type="button"
                  data-testid={TEST_IDS.HISTORY_SEGMENT_ATTEMPTED}
                >
                  Ended early
                </button>
              </div>
            )}

            {sessions.length === 0 ? (
              <p className={styles.empty} data-testid={TEST_IDS.HISTORY_EMPTY_STATE}>
                No sittings yet. Finish a retreat to see it here.
              </p>
            ) : filtered.length === 0 ? (
              <p className={styles.empty}>None in this category.</p>
            ) : (
              <div className={styles.yearGroups}>
                {yearGroups.map(({ year, months, totalCount }) => (
                  <div key={year} className={styles.yearGroup} data-testid={TEST_IDS.HISTORY_YEAR_GROUP}>
                    <button
                      className={styles.yearHeader}
                      onClick={() => toggleYear(year)}
                      type="button"
                      aria-expanded={expandedYears.has(year)}
                    >
                      <span className={styles.yearLabel}>{year}</span>
                      <span className={styles.yearCount}>{totalCount} sitting{totalCount !== 1 ? "s" : ""}</span>
                      <Chevron open={expandedYears.has(year)} />
                    </button>

                    {expandedYears.has(year) && (
                      <div className={styles.monthsInYear}>
                        {months.map(({ monthKey, label, sessions: monthSessions }) => (
                          <div key={monthKey} className={styles.monthGroup} data-testid={TEST_IDS.HISTORY_MONTH_GROUP}>
                            <button
                              className={styles.monthHeader}
                              onClick={() => toggleMonth(monthKey)}
                              type="button"
                              aria-expanded={expandedMonths.has(monthKey)}
                            >
                              <span className={styles.monthHeaderLabel}>{label}</span>
                              <span className={styles.monthCount}>{monthSessions.length}</span>
                              <Chevron open={expandedMonths.has(monthKey)} />
                            </button>

                            {expandedMonths.has(monthKey) && (
                              <div className={styles.monthList}>
                                {monthSessions.map((session, i) => (
                                  <SessionCard
                                    key={session.id || `session-${i}`}
                                    session={session}
                                    index={i}
                                    onSelect={onSelectSession}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.localNote}>kept on this device · synced when online</div>
      </div>
    </div>
  );
}
